import React, { useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { createPortal } from 'react-dom';
import { useAnchoredDropdown } from './selectStyles';

import { ChevronDown } from 'lucide-react';
const CustomSelect = ({
    id,
    value,
    onChange,
    options = [],
    placeholder,
    disabled = false,
    error = false,
    className = '',
    dropdownClassName = '',
    isOpen,
    onToggle,
    testId,
    renderOption,
    renderValue,
    // NOTE: `usePortal` is deliberately not destructured any more. The panel is
    // ALWAYS portaled now (see selectStyles.js), so the prop is accepted and
    // ignored — call sites like components/doctorTabs/AvailabilityTab.jsx keep
    // passing usePortal={true} and keep working, and the ones that never passed it
    // get the fix for free.
}) => {
    const { t } = useTranslation();
    const panelOpen = isOpen && !disabled;
    const { anchorRef, panelRef, panelStyle } = useAnchoredDropdown(panelOpen);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // The panel is portaled to document.body, so it is NOT a DOM
            // descendant of the trigger — testing only the trigger would treat a
            // click on an option as "outside", close the panel on mousedown and
            // swallow the click before it ever selected anything.
            const insidePanel = panelRef.current?.contains(event.target);
            const insideTrigger = anchorRef.current?.contains(event.target);
            if (!insidePanel && !insideTrigger && isOpen) {
                onToggle(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, onToggle, anchorRef, panelRef]);

    const getDisplayValue = () => {
        if (renderValue) {
            return renderValue(value);
        }
        
        if (value === '' || value === null || value === undefined) {
            return placeholder || t("common.select_dot");
        }
        
        const selectedOption = options.find(opt => 
            typeof opt === 'object' ? opt.value === value : opt === value
        );
        
        if (selectedOption) {
            return typeof selectedOption === 'object' ? selectedOption.label : selectedOption;
        }
        
        return value;
    };

    const handleOptionClick = (optionValue) => {
        onChange(optionValue);
        onToggle(false);
    };

    const renderDropdown = () => {
        if (!panelOpen || typeof document === 'undefined') return null;

        // The panel itself is the scroll container here (no search header), so the
        // hook's max-height caps it directly — hence no max-h-60 class any more,
        // which would only have been a second, contradictory ceiling.
        const dropdownContent = (
            <div
                ref={panelRef}
                data-testid={testId ? `${testId}-dropdown` : undefined}
                className={`bg-white rounded-lg shadow-xl p-2 border border-gray-200 overflow-y-auto scrollbar-thin scrollbar-thumb-[#4ABDBD] scrollbar-track-gray-100 ${dropdownClassName}`}
                style={panelStyle}
            >
                {options.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500" data-testid={testId ? `${testId}-empty-text` : undefined}>
                        {placeholder}
                    </div>
                ) : (
                    options.map((option, index) => {
                        const optionValue = typeof option === 'object' ? option.value : option;
                        const optionLabel = typeof option === 'object' ? option.label : option;
                        const isDisabled = typeof option === 'object' && option.disabled;
                        const isSelected = value === optionValue;
                        // Stable per-option testid keyed by value when available, else
                        // the row index — callers can rely on `option-{value}` for
                        // string/number option lists, falling back to index for
                        // object-only enumerations.
                        const optionTestId = testId
                            ? `${testId}-option-${optionValue ?? index}`
                            : undefined;

                        if (renderOption) {
                            return (
                                <div
                                    key={index}
                                    data-testid={optionTestId}
                                    onClick={() => !isDisabled && handleOptionClick(optionValue)}
                                >
                                    {renderOption(option, isSelected, isDisabled)}
                                </div>
                            );
                        }

                        return (
                            <button
                                key={index}
                                type="button"
                                data-testid={optionTestId}
                                onClick={() => !isDisabled && handleOptionClick(optionValue)}
                                disabled={isDisabled}
                                className={`w-full text-left px-4 py-2 text-sm rounded-md mb-1 transition-all duration-300 ${
                                    isSelected
                                        ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                                        : isDisabled
                                        ? 'text-gray-400 cursor-not-allowed opacity-50'
                                        : 'text-gray-700 hover:bg-gradient-to-r hover:from-[#4ABDBD] hover:to-[#3A9B9B] hover:text-white cursor-pointer'
                                }`}
                            >
                                {optionLabel}
                            </button>
                        );
                    })
                )}
            </div>
        );

        return createPortal(dropdownContent, document.body);
    };

    return (
        <div className="relative" data-testid={testId ? `${testId}-container` : undefined}>
            <button
                ref={anchorRef}
                id={id}
                type="button"
                onClick={() => !disabled && onToggle(!isOpen)}
                disabled={disabled}
                data-testid={testId}
                className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 flex items-center justify-between ${
                    error ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
            >
                <span className="truncate" data-testid={testId ? `${testId}-value-text` : undefined}>{getDisplayValue()}</span>
                <ChevronDown
                    data-testid={testId ? `${testId}-chevron-icon` : undefined}
                    className={`ml-2 flex-shrink-0 transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>
            
            {renderDropdown()}
        </div>
    );
};

export default CustomSelect;

